import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Button, IconButton, TextField } from '@volvo/vcdk-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RoleHandle } from '@/routes/_authenticated/_account/members/_components/RoleHandle';

import PageHeader from '@/components/pageheaders/PageHeader';
import RoleTypes from '@/components/RoleTypes';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import FleetAccountRoles from '@/routes/_authenticated/_account/members/_components/FleetAccountRoles';
import FleetRoles from '@/routes/_authenticated/_account/members/_components/FleetRoles';
import {
  getFleetAccountEndUserByIdQueryOptions,
  getFleetAccountRolesQueryOptions,
  getFleetsQueryOptions,
} from '@/service/vpc/queryOptions';
import { fleet } from '@/utils';

export const Route = createFileRoute('/_authenticated/_account/members/$endUserId/')({
  component: MemberPage,
});

function MemberPage() {
  const { endUserId } = useParams({
    from: '/_authenticated/_account/members/$endUserId/',
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fleetAccountId } = useFleetAccount();

  const roleRefs = useRef<Map<string, RoleHandle>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const { data: endUser, isLoading: isEndUserLoading } = useQuery(
    getFleetAccountEndUserByIdQueryOptions({ fleetAccountId: fleetAccountId!, endUserId })
  );
  const { data: fleetAccountRoles, isLoading: isFleetAccountRolesLoading } = useQuery(
    getFleetAccountRolesQueryOptions({ fleetAccountId: fleetAccountId! })
  );
  const { data: fleets } = useQuery(getFleetsQueryOptions({ fleetAccountId: fleetAccountId! }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await Promise.all(Array.from(roleRefs.current.values()).map((ref) => ref.save()));
    } finally {
      setIsSaving(false);
    }
  };

  const currentRoles = fleetAccountRoles?.filter((r) => r.endCustomerId === endUserId) || [];

  return (
    <>
      <PageHeader
        title="Edit member"
        items={[]}
        isLoading={isEndUserLoading || isFleetAccountRolesLoading}
        backButton={<IconButton icon="arrow-left" size="large" onClick={() => navigate({ to: '/members' })} />}
      />
      <div className="flex flex-col gap-6">
        {/* Name */}
        <TextField disabled value={endUser?.firstName + ' ' + endUser?.surname || ''} />
        {/* Email */}
        <TextField disabled value={endUser?.email || ''} />

        {/* Account Roles */}
        <FleetAccountRoles
          ref={(instance) => {
            if (instance) {
              roleRefs.current.set(endUserId, instance);
            }
          }}
          label="Account role"
          placement="bottom"
          isEdit={true}
          currentRoles={currentRoles}
          endUserId={endUserId}
        />

        {/* Fleets with roles */}
        {fleets?.map((f) => (
          <FleetRoles
            key={f.fleetId}
            ref={(instance) => {
              if (instance) {
                roleRefs.current.set(f.fleetId, instance);
              }
            }}
            label={fleet.parseFleetName(f.name).fleetName}
            endUserId={endUserId}
            fleetId={f.fleetId}
            isEdit={true}
          />
        ))}
        <Button variant="primary" disabled={isSaving} loading={isSaving} className="w-full" onClick={handleSave}>
          {t(($) => $.common.save)}
        </Button>
        <RoleTypes />
      </div>
    </>
  );
}
